import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Media, SnakeToCamelObject } from '@browsing/api';
import { BellFilledXS, BellOutlinedXS } from '@lux/zds-icons';
import { Preset, Text } from '@lux/text';
import { Translate } from '@pegasus/translation';

import { UpcomingCampaign } from '@shared/types/mylounge';

import * as S from './upcoming-campaign-card-mini.styles';

interface UpcomingCampaignCardMiniProps {
    campaign: UpcomingCampaign;
    media: Omit<SnakeToCamelObject<Media>, 'path'>;
    bgTransparent?: boolean;
    isPlusVariant?: boolean;
    isSubscribed?: boolean;
    onToggleSubscription?: (campaignId: string) => Promise<void>;
    onClick?: () => void;
    onView?: () => void;
}

const UpcomingCampaignCardMini = ({
    campaign,
    media,
    bgTransparent = false,
    isPlusVariant = false,
    isSubscribed = false,
    onToggleSubscription,
    onClick = () => {},
    onView,
}: UpcomingCampaignCardMiniProps) => {
    const { campaignId } = campaign;
    const { ref, inView } = useInView({
        threshold: 0.5,
        triggerOnce: true,
    });

    useEffect(() => {
        if (inView && onView) {
            onView();
        }
    }, [inView, onView]);

    const handleNotifyClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onToggleSubscription) {
            await onToggleSubscription(campaignId);
        }
    };

    return (
        <S.UpcomingCampaignCardMiniWrapper
            id={campaignId}
            bgTransparent={bgTransparent}
            onClick={onClick}
            ref={ref}
        >
            <S.ImageWrapper>
                <S.CampaignImage src={media.url} alt={media.altText} loading="lazy" />
            </S.ImageWrapper>
            {isPlusVariant && onToggleSubscription && (
                <S.BottomSection>
                    <S.OverflowContainer>
                        <Text preset={Preset.S} bold>
                            {campaign.name}
                        </Text>
                    </S.OverflowContainer>
                    <S.OutOfLuxButton
                        isNotificationRequested={isSubscribed}
                        onClick={handleNotifyClick}
                    >
                        <S.IconWrapper>
                            {isSubscribed ? <BellFilledXS /> : <BellOutlinedXS />}
                        </S.IconWrapper>
                        <S.OutOfLuxButtonText preset={Preset.S} bold>
                            {isSubscribed ? (
                                <Translate message="subscribed" />
                            ) : (
                                <Translate message="notsubscribed" />
                            )}
                        </S.OutOfLuxButtonText>
                    </S.OutOfLuxButton>
                </S.BottomSection>
            )}
        </S.UpcomingCampaignCardMiniWrapper>
    );
};

export { UpcomingCampaignCardMini };
